/**
 * VECTOR TABLES for the share PDF (owner, 2026-10-22: "multi page detailed
 * pdf please, not these screenshots").
 *
 * A table is laid out on the page as text by jspdf-autotable: every row and
 * every column, real selectable text, the header repeated on every page, a
 * Total row riding every page, a table wider than the page split into column
 * bands with the row-label columns repeated on each band (the book layout in
 * buildReportPdf sizes its pages to the table instead, so it never bands),
 * and a cell carrying a `link` tappable. The dress is the
 * leads list's (mint header, hairline rules, quiet zebra), the same the
 * custom report page draws on screen.
 *
 * The table MODEL (`PdfTableModel`) is engine-agnostic: the custom report
 * page builds it from the report's data (every page of rows, not the one on
 * screen); a standard report card builds it from its on-screen <table>.
 *
 * Text that Helvetica cannot print (₹, →, Malayalam…) is rastered by the
 * browser per cell at the same size — see pdfText.
 */
import type { jsPDF } from "jspdf";
import autoTable, { type CellDef, type CellHookData, type UserOptions } from "jspdf-autotable";
import {
  FAINT,
  INK,
  drawLines,
  lineHeightMm,
  longestWordMm,
  rasterLines,
  textWidthMm,
  winAnsiSafe,
  wrapText,
  type Rgb,
} from "./pdfText";

export interface PdfCell {
  text: string;
  colSpan?: number;
  rowSpan?: number;
  align?: "left" | "right" | "center";
  bold?: boolean;
  /** A smaller grey line under the text — a column's date window. */
  sub?: string;
  /** The cell's own tint (a heatmap cell), with the ink to read on it. */
  fill?: Rgb;
  color?: Rgb;
  /** Tapping the cell opens this URL (a number's own leads on the leads page). */
  link?: string;
}

export type PdfRowKind = "row" | "subtotal" | "total";

export interface PdfTableModel {
  head: PdfCell[][];
  body: PdfCell[][];
  /** One kind per body row (default "row"): subtotal and total rows are
   *  tinted and bold. */
  rowKinds?: PdfRowKind[];
  /** Rows that repeat on every page the table spans (the Total row). */
  foot?: PdfCell[][];
  /** The leading row-label columns: left-aligned, and repeated on every
   *  column band when the table is wider than a page. */
  labelColumns: number;
}

export const BODY_PT = 8;
export const HEAD_PT = 7.5;
export const SUB_PT = 6;
const PAD_X = 1.6;
const PAD_Y = 1.3;
const SUB_GAP = 0.6;

const HEAD_FILL: Rgb = [193, 225, 213]; // #C1E1D5 — the list view's header band
const HEAD_INK: Rgb = [55, 71, 65]; // #374741
const RULE: Rgb = [230, 232, 231]; // #e6e8e7
const ZEBRA: Rgb = [247, 249, 248];
const SUBTOTAL_FILL: Rgb = [242, 248, 245]; // #f2f8f5
const SUBTOTAL_RULE: Rgb = [220, 235, 228]; // #dcebe4
const TOTAL_FILL: Rgb = [250, 252, 251]; // #fafcfb

/** How many columns the model spans (colSpans counted). */
export function columnCount(model: PdfTableModel): number {
  let n = 0;
  for (const row of [...model.head, ...model.body, ...(model.foot ?? [])]) {
    n = Math.max(
      n,
      row.reduce((w, c) => w + Math.max(1, c.colSpan ?? 1), 0),
    );
  }
  return n;
}

export interface ColumnPlan {
  widths: number[];
  /** Σ widths as laid out */
  total: number;
  /** Σ of what every column would like — the orientation decision reads it */
  preferred: number;
  /** Σ of every column's minimum — the narrowest the table can be set
   *  without column bands (the book layout sizes its page from it) */
  minimum: number;
  /** true = wider than the page even at every column's minimum: the table
   *  is split into column bands, the label columns repeated on each */
  overflow: boolean;
}

/** Expand a row into per-column slots (colSpan-aware), ignoring rowSpans. */
function slots(row: PdfCell[]): (PdfCell | null)[] {
  const out: (PdfCell | null)[] = [];
  for (const cell of row) {
    const span = Math.max(1, cell.colSpan ?? 1);
    out.push(span === 1 ? cell : null);
    for (let i = 1; i < span; i++) out.push(null);
  }
  return out;
}

/**
 * Column widths for `avail` mm: each column's preferred width when the table
 * fits (stretched a little so a narrow table still reads like the list), its
 * minimum when it does not, and column bands when even the minimums do not
 * fit. Numbers never wrap; long text wraps at the column width.
 */
export function planColumns(doc: jsPDF, model: PdfTableModel, avail: number): ColumnPlan {
  const n = columnCount(model);
  const bodyLine = new Array<number>(n).fill(0);
  const bodyWord = new Array<number>(n).fill(0);
  const headLine = new Array<number>(n).fill(0);
  const headWord = new Array<number>(n).fill(0);
  const subLine = new Array<number>(n).fill(0);
  const subWord = new Array<number>(n).fill(0);
  const pad = PAD_X * 2;

  for (const row of [...model.body, ...(model.foot ?? [])]) {
    slots(row).forEach((cell, j) => {
      if (!cell || !cell.text) return;
      const bold = cell.bold ?? false;
      for (const line of cell.text.split("\n")) {
        bodyLine[j] = Math.max(bodyLine[j]!, textWidthMm(doc, line, BODY_PT, bold) + pad);
      }
      bodyWord[j] = Math.max(bodyWord[j]!, longestWordMm(doc, cell.text, BODY_PT, bold) + pad);
    });
  }
  for (const row of model.head) {
    slots(row).forEach((cell, j) => {
      if (!cell) return;
      const bold = cell.bold ?? false;
      for (const line of cell.text.split("\n")) {
        headLine[j] = Math.max(headLine[j]!, textWidthMm(doc, line, HEAD_PT, bold) + pad);
      }
      headWord[j] = Math.max(headWord[j]!, longestWordMm(doc, cell.text, HEAD_PT, bold) + pad);
      if (cell.sub) {
        subLine[j] = Math.max(subLine[j]!, textWidthMm(doc, cell.sub, SUB_PT) + pad);
        subWord[j] = Math.max(subWord[j]!, longestWordMm(doc, cell.sub, SUB_PT) + pad);
      }
    });
  }

  const min: number[] = [];
  const pref: number[] = [];
  for (let j = 0; j < n; j++) {
    const isLabel = j < model.labelColumns;
    if (!isLabel && bodyLine[j]! <= 16) {
      // A tight column (numbers, short codes): its values never wrap; the
      // header wraps word by word down to its longest word.
      const lo = Math.max(11, bodyLine[j]!, Math.min(Math.max(headWord[j]!, subWord[j]!), 24));
      min.push(lo);
      pref.push(Math.max(lo, Math.min(Math.max(headLine[j]!, subLine[j]!), 34)));
    } else {
      const lo = Math.max(isLabel ? 16 : 14, Math.min(Math.max(bodyWord[j]!, headWord[j]!, subWord[j]!), 34));
      min.push(lo);
      pref.push(Math.max(lo, Math.min(Math.max(bodyLine[j]!, headLine[j]!, subLine[j]!), isLabel ? 58 : 60)));
    }
  }

  const sumMin = min.reduce((a, b) => a + b, 0);
  const sumPref = pref.reduce((a, b) => a + b, 0);
  let widths: number[];
  let overflow = false;
  if (sumPref <= avail) {
    // Fill the page width like the list does, but never blow a small table
    // up past 1.5× — a three-column table wants to stay readable, not wide.
    const stretch = Math.min(avail / sumPref, 1.5);
    widths = pref.map((w) => w * stretch);
  } else if (sumMin <= avail) {
    // Squeeze the measure columns first (their headers wrap, their numbers
    // never do); the row labels give up width only when that is not enough,
    // so a name stays on one line as long as the page allows.
    widths = [...pref];
    const squeeze = (cols: number[], excess: number): number => {
      const room = cols.reduce((a, j) => a + (pref[j]! - min[j]!), 0);
      if (room <= 0) return excess;
      const share = Math.min(1, excess / room);
      for (const j of cols) widths[j] = pref[j]! - (pref[j]! - min[j]!) * share;
      return Math.max(0, excess - room);
    };
    const measures: number[] = [];
    const labels: number[] = [];
    for (let j = 0; j < n; j++) (j < model.labelColumns ? labels : measures).push(j);
    squeeze(labels, squeeze(measures, sumPref - avail));
  } else {
    widths = [...min];
    overflow = true;
  }
  return { widths, total: widths.reduce((a, b) => a + b, 0), preferred: sumPref, minimum: sumMin, overflow };
}

interface RasterPlan {
  lines: string[];
  pt: number;
  bold: boolean;
  color: Rgb;
  align: "left" | "right" | "center";
}

interface HeadPlan {
  labelLines: number;
  subLines: string[];
  align: "left" | "right" | "center";
}

export interface DrawTableOptions {
  x: number;
  y: number;
  width: number;
  /** lowest y a row may reach */
  bottom: number;
  /** y where a continuation page starts */
  top: number;
  plan?: ColumnPlan;
}

/** Draw the table from (x, y); returns the y under its last row. */
export function drawPdfTable(doc: jsPDF, model: PdfTableModel, o: DrawTableOptions): number {
  const plan = o.plan ?? planColumns(doc, model, o.width);
  const widths = plan.widths;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const anySub = model.head.some((row) => row.some((c) => Boolean(c.sub)));
  const headValign = anySub ? "top" : "bottom";

  const rasters = new WeakMap<object, RasterPlan>();
  const heads = new WeakMap<object, HeadPlan>();
  // autotable keeps each input CellDef as `cell.raw`, so the model cell
  // behind an autotable cell is one lookup away — no span arithmetic.
  const sources = new WeakMap<CellDef, PdfCell>();

  const widthOf = (index: number, span: number): number => {
    let w = 0;
    for (let j = index; j < Math.min(widths.length, index + Math.max(1, span)); j++) w += widths[j]!;
    return w;
  };

  const def = (cell: PdfCell, extra: Partial<CellDef["styles"]> = {}): CellDef => {
    const out: CellDef = {
      content: cell.text,
      ...(cell.colSpan && cell.colSpan > 1 ? { colSpan: cell.colSpan } : {}),
      ...(cell.rowSpan && cell.rowSpan > 1 ? { rowSpan: cell.rowSpan } : {}),
      styles: {
        halign: cell.align ?? "left",
        ...(cell.bold ? { fontStyle: "bold" } : {}),
        ...(cell.fill ? { fillColor: cell.fill } : {}),
        ...(cell.color ? { textColor: cell.color } : {}),
        ...extra,
      },
    };
    sources.set(out, cell);
    return out;
  };

  const head = model.head.map((row) => row.map((c) => def(c)));
  let plainIndex = 0;
  const body = model.body.map((row, i) => {
    const kind = model.rowKinds?.[i] ?? "row";
    if (kind === "subtotal") {
      return row.map((c) =>
        def(c, { fillColor: SUBTOTAL_FILL, fontStyle: "bold", lineColor: SUBTOTAL_RULE, lineWidth: { bottom: 0.2 } }),
      );
    }
    if (kind === "total") {
      return row.map((c) => def(c, { fillColor: TOTAL_FILL, fontStyle: "bold", lineWidth: { top: 0.3, bottom: 0.15 } }));
    }
    const zebra = plainIndex++ % 2 === 1;
    return row.map((c) => def(c, zebra && !c.fill ? { fillColor: ZEBRA } : {}));
  });
  const foot = (model.foot ?? []).map((row) => row.map((c) => def(c)));

  const repeat: number[] = [];
  for (let j = 0; j < model.labelColumns && j < widths.length; j++) repeat.push(j);

  const options: UserOptions = {
    startY: o.y,
    margin: { left: o.x, right: Math.max(0, pageW - o.x - o.width), top: o.top, bottom: Math.max(0, pageH - o.bottom) },
    theme: "plain",
    tableLineWidth: 0,
    // The laid-out width (not "wrap"): autotable sizes head cells by their
    // text, so a wrapping header would make it log that the table overflows
    // even though every column carries its planned width.
    tableWidth: Math.min(o.width, plan.total),
    rowPageBreak: "avoid",
    showHead: "everyPage",
    showFoot: foot.length ? "everyPage" : "never",
    horizontalPageBreak: plan.overflow,
    ...(plan.overflow ? { horizontalPageBreakRepeat: repeat, horizontalPageBreakBehaviour: "afterAllRows" } : {}),
    head,
    body,
    foot,
    styles: {
      font: "helvetica",
      fontSize: BODY_PT,
      cellPadding: { top: PAD_Y, bottom: PAD_Y, left: PAD_X, right: PAD_X },
      overflow: "linebreak",
      textColor: INK,
      fillColor: false,
      lineColor: RULE,
      lineWidth: { bottom: 0.15 },
      valign: "middle",
    },
    headStyles: {
      fillColor: HEAD_FILL,
      textColor: HEAD_INK,
      fontSize: HEAD_PT,
      fontStyle: "normal",
      valign: headValign,
      lineWidth: 0,
    },
    footStyles: {
      fillColor: TOTAL_FILL,
      fontStyle: "bold",
      lineWidth: { top: 0.3, bottom: 0.15 },
    },
    columnStyles: Object.fromEntries(widths.map((w, j) => [j, { cellWidth: w }])),
    didParseCell: (data: CellHookData) => {
      const cell = data.cell;
      const raw = cell.raw as CellDef | null;
      if (!raw || typeof raw !== "object") return;
      const source = sources.get(raw);
      const w = widthOf(data.column.index, cell.colSpan) - PAD_X * 2;
      const pt = cell.styles.fontSize;
      const bold = cell.styles.fontStyle === "bold" || cell.styles.fontStyle === "bolditalic";
      const align = cell.styles.halign === "right" ? "right" : cell.styles.halign === "center" ? "center" : "left";
      const text = cell.text.join("\n");

      if (data.section === "head" && source?.sub) {
        const labelLines = wrapText(doc, text, pt, bold, w);
        const subLines = wrapText(doc, source.sub, SUB_PT, false, w);
        heads.set(raw, { labelLines: labelLines.length, subLines, align });
        cell.styles.minCellHeight =
          PAD_Y * 2 + labelLines.length * lineHeightMm(pt) + subLines.length * lineHeightMm(SUB_PT) + SUB_GAP;
      }

      if (text && !winAnsiSafe(text)) {
        const lines = wrapText(doc, text, pt, bold, w);
        const color = toRgb(cell.styles.textColor) ?? INK;
        rasters.set(raw, { lines, pt, bold, color, align });
        // Placeholders keep the row at the right height; the identity
        // overflow keeps fitContent from re-wrapping them.
        cell.text = lines.map(() => " ");
        cell.styles.overflow = (t) => t;
      }
    },
    didDrawCell: (data: CellHookData) => {
      const cell = data.cell;
      const raw = cell.raw as object | null;
      if (!raw || typeof raw !== "object") return;
      const w = cell.width - PAD_X * 2;
      const raster = rasters.get(raw);
      if (raster) {
        const img = rasterLines(raster.lines, raster.pt, { bold: raster.bold, color: raster.color, align: raster.align, widthMm: w });
        if (img) {
          const h = raster.lines.length * lineHeightMm(raster.pt);
          const y =
            cell.styles.valign === "top"
              ? cell.y + PAD_Y
              : cell.styles.valign === "bottom"
                ? cell.y + cell.height - PAD_Y - h
                : cell.y + (cell.height - h) / 2;
          doc.addImage(img.dataUrl, "PNG", cell.x + PAD_X, y, img.widthMm, img.heightMm, undefined, "FAST");
        }
      }
      const headPlan = heads.get(raw);
      if (headPlan && data.section === "head") {
        const y = cell.y + PAD_Y + headPlan.labelLines * lineHeightMm(cell.styles.fontSize) + SUB_GAP;
        drawLines(doc, headPlan.subLines, cell.x + PAD_X, y, SUB_PT, { color: FAINT, align: headPlan.align, widthMm: w });
      }
      // The whole cell is the tap target, on every page a Total row repeats on.
      const link = data.section !== "head" ? sources.get(raw as CellDef)?.link : undefined;
      if (link) doc.link(cell.x, cell.y, cell.width, cell.height, { url: link });
    },
  };

  autoTable(doc, options);
  const finalY = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY;
  return typeof finalY === "number" ? finalY : o.y;
}

function toRgb(color: unknown): Rgb | null {
  if (Array.isArray(color) && color.length === 3 && color.every((n) => typeof n === "number")) {
    return [color[0] as number, color[1] as number, color[2] as number];
  }
  if (typeof color === "number") return [color, color, color];
  return null;
}
